import { type FC } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Device, DEVICE_LIST } from "../../../../shared/configs/device-types";

type DeviceSelectionScreenProps = {
  onDeviceSelect: (deviceKey: Device) => void;
};

const DeviceCard: FC<{ device: Device; onClick: (key: string) => void }> = ({
  device,
  onClick,
}) => {
  const { t } = useTranslation();
  return (
    <motion.button
      type="button"
      onClick={() => onClick(device.key)}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.98 }}
      className="group bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl px-6 py-8 flex flex-col items-center justify-center text-center shadow-[0_0_25px_-10px_rgba(0,0,0,0.5)] transition-all duration-200 hover:border-cyan-400/60 hover:shadow-[0_0_25px_-5px_rgba(34,211,238,0.4)]"
    >
      <span className="text-5xl mb-4 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.4)] transition-transform duration-200 group-hover:scale-110">
        {device.icon}
      </span>
      <span className="text-lg font-semibold text-gray-100 mb-1">
        {t(`devices.${device.key}.name`)}
      </span>
      <span className="text-sm text-gray-400">
        {device.unit}
      </span>
    </motion.button>
  );
};

export const DeviceSelection: FC<DeviceSelectionScreenProps> = ({
  onDeviceSelect,
}) => {
  const { t } = useTranslation();
  return (
    <motion.div
      id="device-selection-screen"
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] w-full max-w-[800px] flex flex-col items-center text-center bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-gray-100 p-10 rounded-2xl border border-gray-800 shadow-[0_0_40px_-15px_rgba(0,0,0,0.6)] space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.4)]">
          {t('device.selection.title')}
        </h1>
        <p className="text-gray-400 text-sm max-w-md mx-auto">
          {t('device.selection.subtitle')}
        </p>
      </div>

      <div
        id="device-buttons"
        className="grid grid-cols-1 sm:grid-cols-2 gap-8 w-full mt-4"
      >
        {DEVICE_LIST.map((device) => (
          <DeviceCard
            key={device.key}
            device={device}
            onClick={() => onDeviceSelect(device)}
          />
        ))}
      </div>
    </motion.div>
  );
};
